import { Link } from 'react-router-dom';
import { ArrowRight, Check, Sparkles } from 'lucide-react';
import MarketingLayout from './shared/MarketingLayout';
import { PageHero, Section, SectionHeader, CTAStrip } from './shared/blocks';
import SEO from './shared/SEO';
import { Button } from '../../components/ui/button';
import { AI_TOOLS, LANGUAGES } from './content';

const TONE_BG = new Proxy({}, { get: () => 'from-mk-ice to-white dark:from-mk-navy dark:to-mk-midnight' });
const TONE_ICON = new Proxy({}, { get: () => 'bg-mk-blue text-white' });

export default function AIToolsForTeachersPage() {
  return (
    <MarketingLayout>
      <SEO
        title="AI Tools for Teachers | Lesson plans, worksheets, quizzes & more"
        description="Six AI tools for Indian teachers: lesson plan generator, worksheet generator, quiz generator, question paper generator, assignment generator, and teaching material generator — all multilingual, all board-aligned."
        path="/ai-tools-for-teachers"
        keywords={['AI tools for teachers', 'AI teaching assistant', 'lesson plan AI', 'worksheet AI', 'quiz AI', 'question paper AI']}
        breadcrumbs={[{ name: 'Home', url: '/' }, { name: 'AI Tools for Teachers', url: '/ai-tools-for-teachers' }]}
      />
      <PageHero
        eyebrow="AI Tools for Teachers"
        title="The teacher productivity platform."
        accent="Built for Indian classrooms."
        subtitle="Six AI tools that replace the most repetitive parts of teacher prep — multilingual, board-aware, and ready to download as DOCX or PDF in minutes."
        testid="ai-tools-hero"
      >
        <div className="flex flex-col sm:flex-row gap-3">
          <Link to="/signup" className="w-full sm:w-auto">
            <Button size="lg" className="bg-mk-blue hover:bg-mk-deep text-white h-12 px-7 font-semibold w-full sm:w-auto" data-testid="ai-tools-cta-primary">
              Try Free <ArrowRight className="ml-1.5 h-4 w-4" />
            </Button>
          </Link>
          <Link to="/pricing" className="w-full sm:w-auto">
            <Button size="lg" variant="outline" className="h-12 px-7 font-semibold w-full sm:w-auto border-2">See pricing</Button>
          </Link>
        </div>
      </PageHero>

      {AI_TOOLS.map((tool, idx) => {
        const Icon = tool.icon;
        const isReverse = idx % 2 === 1;
        return (
          <Section key={tool.id} tone={idx % 2 === 0 ? 'default' : 'muted'} id={tool.id}>
            <div className={`grid lg:grid-cols-2 gap-10 items-center ${isReverse ? 'lg:[&>div:first-child]:order-2' : ''}`}>
              <div>
                <div className={`inline-flex h-12 w-12 rounded-xl items-center justify-center mb-4 ${TONE_ICON[tool.tone]}`}>
                  <Icon className="h-6 w-6" />
                </div>
                <h2 className="text-2xl sm:text-4xl font-bold tracking-tight">{tool.name}</h2>
                <p className="mt-3 text-base text-mk-muted dark:text-slate-300 leading-relaxed">{tool.summary}</p>
                <ul className="mt-5 space-y-2.5">
                  {tool.bullets.map((b, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-sm">
                      <Check className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" /> {b}
                    </li>
                  ))}
                </ul>
                <div className="mt-6">
                  <Link to="/signup">
                    <Button className="bg-mk-navy hover:bg-slate-800 text-white" data-testid={`ai-tools-${tool.id}-cta`}>
                      Try {tool.name} <ArrowRight className="ml-1.5 h-4 w-4" />
                    </Button>
                  </Link>
                </div>
              </div>
              <div className={`relative rounded-3xl bg-gradient-to-br ${TONE_BG[tool.tone]} p-8 sm:p-10 border border-mk-line/60 dark:border-mk-edge`}>
                <div className="rounded-2xl bg-white dark:bg-mk-navy border border-mk-line dark:border-mk-edge shadow-xl p-5">
                  <div className="flex items-center gap-2 mb-3 pb-3 border-b border-mk-line dark:border-mk-edge">
                    <Icon className="h-4 w-4 text-mk-blue" />
                    <span className="text-xs font-mono text-slate-500">{tool.name.toLowerCase().replace(/\s+/g, '-')}.docx</span>
                  </div>
                  <div className="space-y-2">
                    <div className="h-3 rounded bg-slate-100 dark:bg-slate-800 w-3/4" />
                    <div className="h-3 rounded bg-slate-100 dark:bg-slate-800 w-full" />
                    <div className="h-3 rounded bg-slate-100 dark:bg-slate-800 w-5/6" />
                    <div className="h-3 rounded bg-slate-100 dark:bg-slate-800 w-2/3" />
                  </div>
                  <div className="mt-4 pt-3 border-t border-mk-line dark:border-mk-edge flex items-center gap-2 text-xs text-slate-500">
                    <Sparkles className="h-3.5 w-3.5 text-mk-blue" /> Generated in 12 seconds · {LANGUAGES[idx % LANGUAGES.length].label}
                  </div>
                </div>
              </div>
            </div>
          </Section>
        );
      })}

      <CTAStrip
        title="Pick a tool. Hit generate."
        subtitle="Free starter tier — try every AI tool with one click."
        primary={{ label: 'Sign up free', href: '/signup' }}
        secondary={{ label: 'Founder chat', wa: 'Hi Founder! I want to try the AI tools.' }}
        testid="ai-tools-final-cta"
      />
    </MarketingLayout>
  );
}
